import { Fragment, useCallback, useMemo, useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import { produce } from 'immer';
import {
  SimpleTable,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableSkeletonRows,
} from '@antimatterhq/ui/components/simpleTable';
import { Badge } from '@antimatterhq/ui/components/badge';
import { workspace_v1_WorkspaceConfig } from '@antimatterhq/asl-api-ts-v1';
import {
  useGetWorkspaceConfig,
  useOptimisticUpdateWorkspaceConfig,
} from '@/hooks/workspace/useGetWorkspaceConfig';
import { useUpdateWorkspaceConfig } from '@/hooks/workspace/useUpdateWorkspaceConfig';
import ManagedRetentionSchemaDialog from '@/components/dialog/configureWorkspace/managedRetention/schema/ManagedRetentionSchemaDialog';
import {
  getNewManagedRetentionOverride,
  getNewManagedRetentionSchema,
} from '@/lib/workspaceConfig/managedRetention';
import { bumpMetadataVersion } from '@/utils/util';
import {
  WorkspaceConfig_ManagedRetention_Override,
  WorkspaceConfig_ManagedRetention_Schema,
} from '@/types/api';
import { EditManagedRetentionOverrideRow } from '@/components/table/rows/managedRetentionOverride/EditManagedRetentionOverrideRow';
import { RowButton } from '@/components/button/row/RowButton';

export default function ManagedRetention() {
  const { data: workspaceConfig, isPending } = useGetWorkspaceConfig();
  const { mutateAsync: updateConfig } = useUpdateWorkspaceConfig();
  const optimisticUpdate = useOptimisticUpdateWorkspaceConfig();

  const [activeSchema, setActiveSchema] = useState<
    WorkspaceConfig_ManagedRetention_Schema | undefined
  >();
  const [activeOverride, setActiveOverride] = useState<
    | { override: WorkspaceConfig_ManagedRetention_Override; schema: string }
    | undefined
  >(undefined);

  const disableRowControls = !!activeOverride;

  const schemas = useMemo(() => {
    return workspaceConfig?.spec.managedRetention || [];
  }, [workspaceConfig]);

  const save = useCallback(
    ({
      config,
      success,
      error,
    }: {
      config: workspace_v1_WorkspaceConfig;
      success: string;
      error: string;
    }) => {
      optimisticUpdate(config);
      updateConfig(config, {
        toasts: {
          success,
          error,
        },
        onError: async () => {
          optimisticUpdate(workspaceConfig!);
        },
      });
      setActiveSchema(undefined);
      setActiveOverride(undefined);
    },
    [optimisticUpdate, updateConfig, workspaceConfig],
  );

  const editSchema = useCallback(
    (schema: WorkspaceConfig_ManagedRetention_Schema) => {
      const config = produce(workspaceConfig!, (draft) => {
        const managedRetention = draft.spec.managedRetention || [];

        const index = managedRetention.findIndex(
          (s) => s.schema === activeSchema?.schema,
        );
        managedRetention[index] = schema;
        bumpMetadataVersion(draft);
      });
      save({
        config,
        success: 'Successfully updated managed retention schema',
        error: 'Failed to update managed retention schema',
      });
    },
    [workspaceConfig, save, activeSchema],
  );

  const createSchema = useCallback(
    (schema: WorkspaceConfig_ManagedRetention_Schema) => {
      const config = produce(workspaceConfig!, (draft) => {
        const managedRetention = draft.spec.managedRetention || [];
        managedRetention.push(schema);
        bumpMetadataVersion(draft);
      });
      save({
        config,
        success: 'Successfully added managed retention schema',
        error: 'Failed to add managed retention schema',
      });
    },
    [workspaceConfig, save],
  );

  const onSaveSchema = useCallback(
    async (newSchema: WorkspaceConfig_ManagedRetention_Schema) => {
      if (activeSchema?.schema) {
        editSchema(newSchema);
        return;
      }

      createSchema(newSchema);
    },
    [createSchema, activeSchema, editSchema],
  );

  const onDeleteSchema = useCallback(
    async (schema: WorkspaceConfig_ManagedRetention_Schema) => {
      const config = produce(workspaceConfig!, (draft) => {
        draft.spec.managedRetention = (
          draft.spec.managedRetention || []
        ).filter((s) => s.schema !== schema.schema);
        bumpMetadataVersion(draft);
      });

      save({
        config,
        success: 'Successfully deleted managed retention schema',
        error: 'Failed to delete managed retention schema',
      });
    },
    [save, workspaceConfig],
  );

  const editOverride = useCallback(
    (override: WorkspaceConfig_ManagedRetention_Override) => {
      if (!workspaceConfig || !workspaceConfig.spec.managedRetention) {
        return;
      }

      const { managedRetention } = workspaceConfig.spec;

      const schemaIndex = managedRetention.findIndex(
        (s) => s.schema === activeOverride?.schema,
      );

      if (schemaIndex === -1 || schemaIndex === undefined) {
        console.error('schema not found', managedRetention, activeOverride);
        return;
      }

      const index =
        managedRetention[schemaIndex].overrides?.findIndex(
          (s) => s === activeOverride?.override,
        ) || -1;

      const config = produce(workspaceConfig, (draft) => {
        draft.spec.managedRetention = draft.spec.managedRetention || [];
        draft.spec.managedRetention[schemaIndex].overrides =
          draft.spec.managedRetention[schemaIndex].overrides || [];

        draft.spec.managedRetention[schemaIndex].overrides[index] = override;

        bumpMetadataVersion(draft);
      });

      save({
        config,
        success: 'Successfully updated managed retention override',
        error: 'Failed to update managed retention override',
      });
    },
    [activeOverride, save, workspaceConfig],
  );

  const createOverride = useCallback(
    (override: WorkspaceConfig_ManagedRetention_Override) => {
      if (!workspaceConfig || !workspaceConfig.spec.managedRetention) {
        return;
      }

      const { managedRetention } = workspaceConfig.spec;

      const schemaIndex = managedRetention.findIndex(
        (s) => s.schema === activeOverride?.schema,
      );

      if (schemaIndex === -1 || schemaIndex === undefined) {
        console.error('schema not found', managedRetention, activeOverride);
        return;
      }

      const config = produce(workspaceConfig, (draft) => {
        draft.spec.managedRetention = draft.spec.managedRetention || [];
        draft.spec.managedRetention[schemaIndex].overrides =
          draft.spec.managedRetention[schemaIndex].overrides || [];

        draft.spec.managedRetention[schemaIndex].overrides.push(override);
        bumpMetadataVersion(draft);
      });

      save({
        config,
        success: 'Successfully created managed retention override',
        error: 'Failed to create managed retention override',
      });
    },
    [activeOverride, save, workspaceConfig],
  );

  const onSaveOverride = useCallback(
    async (newOverride: WorkspaceConfig_ManagedRetention_Override) => {
      if (activeOverride?.override.table) {
        editOverride(newOverride);
        return;
      }
      createOverride(newOverride);
    },
    [activeOverride, editOverride, createOverride],
  );

  const onDeleteOverride = useCallback(
    async ({
      schema,
      override,
    }: {
      schema: string;
      override: WorkspaceConfig_ManagedRetention_Override;
    }) => {
      if (!workspaceConfig || !workspaceConfig.spec.managedRetention) {
        return;
      }

      const { managedRetention } = workspaceConfig.spec;

      const schemaIndex = managedRetention.findIndex(
        (s) => s.schema === schema,
      );

      if (schemaIndex === -1 || schemaIndex === undefined) {
        console.error('schema not found', managedRetention, activeOverride);
        return;
      }

      const overrides =
        managedRetention[schemaIndex].overrides?.filter(
          (s) => s !== override,
        ) || [];

      const config = produce(workspaceConfig, (draft) => {
        draft.spec.managedRetention = draft.spec.managedRetention || [];

        draft.spec.managedRetention[schemaIndex].overrides = overrides;

        bumpMetadataVersion(draft);
      });

      save({
        config,
        success: 'Successfully deleted managed retention override',
        error: 'Failed to delete managed retention override',
      });
    },
    [workspaceConfig, activeOverride, save],
  );

  return (
    <>
      <SimpleTable className="w-full" fixed>
        <SimpleTableHeader>
          <SimpleTableRow hover={false}>
            <SimpleTableHead className="w-[250px] font-bold">
              Name
            </SimpleTableHead>
            <SimpleTableHead className="font-bold">Catalog</SimpleTableHead>
            <SimpleTableHead className="w-[150px] font-bold">
              Time column
            </SimpleTableHead>
            <SimpleTableHead className="w-[150px] font-bold">
              Duration
            </SimpleTableHead>
            <SimpleTableHead className="w-[150px]"></SimpleTableHead>
          </SimpleTableRow>
        </SimpleTableHeader>
        <SimpleTableBody>
          {!isPending &&
            schemas.map((schema) => (
              <Fragment key={schema.schema}>
                <SimpleTableRow className="h-[31px] hover:bg-gray-100 dark:hover:bg-gray-800">
                  <SimpleTableCell className="w-[250px]">
                    {schema.schema}
                  </SimpleTableCell>
                  <SimpleTableCell className="relative w-auto truncate">
                    {schema.catalog}
                  </SimpleTableCell>
                  <SimpleTableCell className="w-[150px]">
                    {schema.column}
                  </SimpleTableCell>
                  <SimpleTableCell className="w-[150px]">
                    {schema.duration}
                  </SimpleTableCell>
                  <SimpleTableCell className="relative w-[150px]">
                    <div className="invisible flex justify-end gap-2">
                      <RowButton
                        icon="lucide:plus"
                        onClick={() =>
                          setActiveOverride({
                            schema: schema.schema,
                            override: getNewManagedRetentionOverride(),
                          })
                        }
                        disabled={disableRowControls}
                      />
                      <RowButton
                        icon="antimatterhq:pencil"
                        onClick={() => setActiveSchema(schema)}
                        disabled={disableRowControls}
                      />
                      <RowButton
                        icon="antimatterhq:trash"
                        onClick={() => onDeleteSchema(schema)}
                        disabled={disableRowControls}
                      />
                    </div>
                  </SimpleTableCell>
                </SimpleTableRow>
                {(schema.overrides || []).map((override) => {
                  if (override === activeOverride?.override) {
                    return (
                      <SimpleTableRow key={override.table}>
                        <SimpleTableCell colSpan={5}>
                          <EditManagedRetentionOverrideRow
                            onCancel={() => setActiveOverride(undefined)}
                            override={override}
                            onSave={onSaveOverride}
                          />
                        </SimpleTableCell>
                      </SimpleTableRow>
                    );
                  }
                  return (
                    <SimpleTableRow
                      key={override.table}
                      className="hover-row h-[31px] hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <SimpleTableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="filled" color="info">
                            Override
                          </Badge>
                          {override.table}
                        </div>
                      </SimpleTableCell>
                      <SimpleTableCell>--</SimpleTableCell>
                      <SimpleTableCell>{override.column}</SimpleTableCell>
                      <SimpleTableCell>{override.duration}</SimpleTableCell>
                      <SimpleTableCell>
                        <div className="invisible flex justify-end gap-2">
                          <RowButton
                            icon="antimatterhq:pencil"
                            onClick={() =>
                              setActiveOverride({
                                schema: schema.schema,
                                override,
                              })
                            }
                            disabled={disableRowControls}
                          />
                          <RowButton
                            icon="antimatterhq:trash"
                            onClick={() =>
                              onDeleteOverride({
                                schema: schema.schema,
                                override,
                              })
                            }
                            disabled={disableRowControls}
                          />
                        </div>
                      </SimpleTableCell>
                    </SimpleTableRow>
                  );
                })}
                {activeOverride?.schema === schema.schema &&
                  !activeOverride.override.table && (
                    <SimpleTableRow
                      key={`override-${schema.schema}`}
                      className="m-0 p-0"
                    >
                      <SimpleTableCell colSpan={5} className="m-0 p-0">
                        <EditManagedRetentionOverrideRow
                          onCancel={() => setActiveOverride(undefined)}
                          override={activeOverride.override}
                          onSave={onSaveOverride}
                        />
                      </SimpleTableCell>
                    </SimpleTableRow>
                  )}
              </Fragment>
            ))}
          {isPending && (
            <SimpleTableSkeletonRows cols={5} height={35} rows={5} />
          )}
        </SimpleTableBody>
      </SimpleTable>

      <Spacer size="lg" />

      <div className="flex justify-end gap-2">
        <Button
          variant="default"
          leftIcon="radix-icons:plus"
          onClick={() => setActiveSchema(getNewManagedRetentionSchema())}
        >
          Add schema
        </Button>
      </div>

      {!!activeSchema && (
        <ManagedRetentionSchemaDialog
          isOpen={true}
          onSave={onSaveSchema}
          onCancel={() => setActiveSchema(undefined)}
          schema={activeSchema}
          isEdit={!!activeSchema?.schema}
        />
      )}
    </>
  );
}
